import { apiUrl, getApiBaseUrl } from './getApiUrl';

const SESSION_KEY = 'corebids_session_id';
const UTM_KEY = 'corebids_utms';

export type AnalyticsEventPayload = {
  path?: string;
  userId?: string;
  projectId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  durationMs?: number;
};

const safeRandomId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const getAnalyticsSessionId = (): string => {
  if (typeof window === 'undefined') return safeRandomId();
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const next = safeRandomId();
    localStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return safeRandomId();
  }
};

export const persistUtmParams = (search: string) => {
  if (typeof window === 'undefined' || !search) return;
  try {
    const params = new URLSearchParams(search);
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    if (utmSource || utmMedium || utmCampaign) {
      const payload = JSON.stringify({ utmSource, utmMedium, utmCampaign });
      localStorage.setItem(UTM_KEY, payload);
    }
  } catch {
    // ignore
  }
};

const getStoredUtms = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      utmSource: parsed?.utmSource || undefined,
      utmMedium: parsed?.utmMedium || undefined,
      utmCampaign: parsed?.utmCampaign || undefined,
    };
  } catch {
    return {};
  }
};

// CB:BEGIN analytics-dev-gate
const canSendAnalytics = (): boolean => {
  const env = (import.meta as any)?.env || {};
  if (env.VITE_DISABLE_ANALYTICS === 'true') return false;

  const base = getApiBaseUrl();
  if (base.includes('api.dev.corebids.com')) {
    return env.VITE_ALLOW_DEV_ANALYTICS === 'true';
  }

  return true;
};
// CB:END analytics-dev-gate

export const trackAnalyticsEvent = async (
  eventType: string,
  payload: AnalyticsEventPayload = {}
): Promise<void> => {
  if (!canSendAnalytics()) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[analytics] skipped (disabled or dev host)');
    }
    return;
  }
  try {
    const sessionId = getAnalyticsSessionId();
    const utms = getStoredUtms();
    await fetch(apiUrl('/api/analytics/events'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        sessionId,
        eventType,
        ...utms,
        ...payload,
      }),
    });
  } catch (error: any) {
    // CB:BEGIN analytics-quiet-when-api-down
    const isNetworkError =
      error?.message === 'API_UNREACHABLE' ||
      (error instanceof TypeError && (error?.message === 'Failed to fetch' || error?.message?.includes?.('fetch'))) ||
      (error?.message && /fetch|network|connection/i.test(String(error.message)));
    if (import.meta.env?.DEV && isNetworkError) return;
    // CB:END analytics-quiet-when-api-down
    if (process.env.NODE_ENV === 'development') {
      console.warn('[analytics] failed to send event', eventType, error);
    }
  }
};

